// ============================================================
// HOUSE OF CARDS — ROTTEN MANOR THEME v2 — AAA PASS
// Flesh in the walls, mold in the grain, bone-white text,
// ember light from a fire that never finished its job.
// v2 adds: Cinzel display face, gold/trophy tokens, item rarity,
// and ROOM_SCENES — full first-person backdrop per room.
// Same contract as Terror's theme.js: import tokens, never hardcode.
// ============================================================

// ── RM — Rotten Manor palette (ALL screens use this) ─────────
export const RM = {
  // Char — burnt house bones, background depths
  char0: "#070504",   // deepest soot
  char1: "#0d0907",   // charred beam
  char2: "#160e0a",   // smoke-stained plaster
  char3: "#221511",   // warm rot midtone
  charBorder: "#33201a",

  // Flesh — the living walls
  flesh:      "#8b3a3a",
  fleshDeep:  "#5c1f24",
  fleshGlow:  "#c4565a",  // vein highlight, danger accent
  vein:       "#6e2333",

  // Mold — green decay, vines, mushrooms
  mold:       "#4a5d3a",
  moldBright: "#7da05c",  // success / interactable accent
  moldDim:    "#2c3a22",

  // Bone — text on dark rot
  bone:      "#d8cbb3",   // primary text
  boneDim:   "#9c8d72",   // secondary
  boneFaint: "#5e5343",   // muted / locked

  // Ember — fire memory, warnings, QTE prompts
  ember:       "#d97a2b",
  emberBright: "#f0a04b",
  emberDim:    "#6e3a12",

  // Gold — trophies, keys, the toll (NEW)
  gold:       "#b8923f",
  goldBright: "#e8c060",

  // Psychosis ramp — the meter climbs green → glowing purple across
  // five tiers, matched 1:1 to the five heartbeat tracks. Hand-tuned
  // so each step reads distinctly even at the small HUD size.
  psyRamp0: "#7da05c", // LUCID     — mold green (calm)
  psyRamp1: "#b6a347", // FRAYING   — sickly chartreuse-gold
  psyRamp2: "#cf7a3a", // SLIPPING  — ember orange
  psyRamp3: "#b14f8a", // SPLITTING — magenta-rot
  psyRamp4: "#9a4fe0", // BREAKING  — glowing purple (the break)

  font:   "'IM Fell English', 'Palatino Linotype', serif",
  fontD:  "'IM Fell English SC', 'Palatino Linotype', serif",
  fontT:  "'Cinzel', 'IM Fell English SC', serif", // big titles only
};

// ── Manor background — fallback / story screens ──────────────
export const MANOR_BG = [
  "repeating-linear-gradient(115deg, transparent, transparent 46px, rgba(110,35,51,0.06) 46px, rgba(110,35,51,0.06) 48px)",
  "radial-gradient(ellipse at 8% 92%, rgba(74,93,58,0.20) 0%, transparent 45%)",
  "radial-gradient(ellipse at 92% 88%, rgba(44,58,34,0.22) 0%, transparent 40%)",
  "radial-gradient(ellipse at 50% -10%, rgba(92,31,36,0.30) 0%, transparent 55%)",
  "#070504",
].join(", ");

// ── Fire background — prologue only ──────────────────────────
export const FIRE_BG = [
  "radial-gradient(ellipse at 20% 100%, rgba(217,122,43,0.45) 0%, transparent 55%)",
  "radial-gradient(ellipse at 80% 95%, rgba(196,86,90,0.35) 0%, transparent 50%)",
  "radial-gradient(ellipse at 50% 110%, rgba(240,160,75,0.30) 0%, transparent 60%)",
  "#0d0907",
].join(", ");

// ── Glow helpers ──────────────────────────────────────────────
export const veinGlow = (c = RM.fleshGlow) =>
  `0 0 16px ${c}40, inset 0 1px 0 ${c}22`;

export const emberGlow = (c = RM.ember) =>
  `0 0 0 1px ${c}88, 0 0 14px ${c}55, 0 0 28px ${c}22`;

// ── Per-room tint — kept for closeup plates / compat ─────────
export const ROOM_TINTS = {
  hall:    "rgba(110,35,51,0.22)",
  nursery: "rgba(125,160,92,0.14)",
  study:   "rgba(217,122,43,0.12)",
  kitchen: "rgba(74,93,58,0.18)",
  bath:    "rgba(70,110,104,0.16)",
  master:  "rgba(86,96,128,0.16)",
  cellar:  "rgba(140,60,40,0.20)",
  landing:      "rgba(184,146,63,0.16)",
  longkitchen:  "rgba(217,122,43,0.16)",
  nurserybelow: "rgba(232,192,96,0.16)",
};

// ── ROOM_SCENES — first-person backdrop per room (NEW) ───────
// Each room is built from 3 planes (back wall, ceiling, floor)
// rendered in real CSS 3D perspective by SceneEngine, plus a
// breathing fog color. When Midjourney plates land (Phase 5),
// `back` simply becomes an image URL layered under the glow.
const mk = (glow, wallA, wallB, floorA, floorB, fog) => ({
  back: [
    `radial-gradient(ellipse at 50% 58%, ${glow} 0%, transparent 64%)`,
    `linear-gradient(180deg, ${wallA} 0%, ${wallB} 100%)`,
  ].join(", "),
  floor: [
    // floorboards running away from the camera (converge via 3D)
    "repeating-linear-gradient(90deg, rgba(0,0,0,0.45) 0px 3px, transparent 3px 72px)",
    // plank end-seams
    "repeating-linear-gradient(0deg, rgba(0,0,0,0.30) 0px 2px, transparent 2px 120px)",
    `linear-gradient(180deg, ${floorA} 0%, ${floorB} 100%)`,
  ].join(", "),
  ceil: [
    "repeating-linear-gradient(90deg, rgba(0,0,0,0.30) 0px 2px, transparent 2px 96px)",
    `linear-gradient(0deg, ${wallB} 0%, #050302 100%)`,
  ].join(", "),
  fog,
});

export const ROOM_SCENES = {
  hall:    mk("rgba(110,35,51,0.45)", "#1b0e0e", "#0b0706", "#1e120c", "#090605", "rgba(110,35,51,0.18)"),
  nursery: mk("rgba(125,160,92,0.22)", "#13150a", "#0a0b06", "#171009", "#080604", "rgba(125,160,92,0.10)"),
  study:   mk("rgba(217,122,43,0.22)", "#1a1108", "#0c0805", "#1c1208", "#090604", "rgba(217,122,43,0.10)"),
  kitchen: mk("rgba(74,93,58,0.28)",  "#11140b", "#090a06", "#151107", "#070603", "rgba(74,93,58,0.14)"),
  bath:    mk("rgba(70,110,104,0.26)","#0d1413", "#070b0a", "#101414", "#060808", "rgba(70,110,104,0.16)"),
  master:  mk("rgba(86,96,128,0.24)", "#10121c", "#08090e", "#13101a", "#070608", "rgba(86,96,128,0.14)"),
  cellar:  mk("rgba(150,62,38,0.32)", "#180c08", "#080503", "#1b0e07", "#060402", "rgba(150,62,38,0.18)"),

  // ── Chapter Two — the descent. Warmer the deeper you go. ────
  landing:      mk("rgba(184,146,63,0.30)", "#15100a", "#0a0805", "#181009", "#080604", "rgba(184,146,63,0.16)"),
  longkitchen:  mk("rgba(217,122,43,0.34)", "#1a1207", "#0c0804", "#1d1308", "#090603", "rgba(217,122,43,0.18)"),
  nurserybelow: mk("rgba(232,192,96,0.30)", "#171109", "#0b0805", "#1a1209", "#090604", "rgba(232,192,96,0.15)"),
};

// ── ITEM RARITY — inventory border color coding (NEW) ────────
// common = bone · clue = mold · tool = ember · key = gold · relic = flesh
export const ITEM_RARITY = {
  rubber_ball:      "common",
  torn_page:        "clue",
  tinder_box:       "tool",
  ice_block:        "tool",
  brass_valve:      "tool",
  iron_crank:       "tool",
  crescent_key:     "key",
  rust_key:         "key",
  tarnished_key:    "key",
  black_key:        "key",
  tarnished_locket: "relic",
  painted_rock:     "relic",
  // Chapter Two
  ember_jar:        "tool",
  bone_whistle:     "tool",
  cradle_key:       "key",
  brothers_locket:  "relic",
};

export const RARITY_COLORS = {
  common: RM.boneDim,
  clue:   RM.moldBright,
  tool:   RM.ember,
  key:    RM.goldBright,
  relic:  RM.fleshGlow,
};
